import React, { ReactElement, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Form, FormGroup, Label } from 'reactstrap';
import { Close, Button, ReactSelectOverrides } from '@bphxd/ds-core-react';
import { useQuery } from '@apollo/client';
import { GET_JOB_NAMES_BY_ASSET } from '../../../graph-queries/tar/job';
import Select from 'react-select';
import { TAspect } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';

type Props<T extends TAspect> = {
  selection: T[];
  title: string;
  onSubmit: (selection: T[], jobIds: number[]) => Promise<unknown>;
  renderButton: (onClick: () => void) => ReactElement;
};

function JobLinkModal<T extends TAspect>({ selection, title, onSubmit, renderButton }: Props<T>) {
  const asset = useSelectedAsset();
  const { data } = useQuery(GET_JOB_NAMES_BY_ASSET, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset,
  });
  const [modal, setModal] = useState(false);
  const toggle = () => setModal((v) => !v);

  // TODO: For supporting multiple selection, we need a better calc
  const linkedJobIds = selection?.[0]?.tarJobs?.map((job) => job.jobId) || [];

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();

    const data = new FormData(event.target as HTMLFormElement);
    const jobIds = ((data.getAll('jobId') as string[]) || [])
      .filter((j) => j !== '')
      .map((jobId) => parseInt(jobId));
    onSubmit(selection, jobIds)
      .then(() => {
        setModal(false);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const options =
    data?.jobsByAssetId?.map((job) => ({
      value: job?.jobId || -1,
      label: job ? `${job?.jobId} - ${job?.jobDescription}` : '',
    })) || [];

  const defaultValue = options.filter((opt) => linkedJobIds.includes(opt.value));

  return (
    <>
      {renderButton(() => setModal(true))}
      <Modal size="md" isOpen={modal} toggle={toggle} className="modal-dialog-centered">
        <ModalHeader className="border-0 px-7" close={<Close onClick={toggle} />}>
          {title}
        </ModalHeader>
        <ModalBody className="p-5 pt-0">
          <Form className="row g-3 px-5" onSubmit={handleSubmit}>
            <div className="col-md-12">
              <FormGroup>
                <Label for="link-job">Select Job(s) to link</Label>
                <Select
                  id="link-job"
                  name="jobId"
                  isMulti
                  defaultValue={defaultValue}
                  options={options}
                  components={{
                    ...ReactSelectOverrides.default,
                    IndicatorSeparator: ReactSelectOverrides.NoIndicatorSeparator,
                  }}
                  placeholder="Select from the job list"
                  classNamePrefix="react-select"
                />
              </FormGroup>
            </div>

            <div className="col-md-12">
              <Button type="submit" size="lg" className="w-100 my-4" theme="darker" level="primary">
                Create Link
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  );
}

export default JobLinkModal;
